/**
 * 📅🔔 WeighInReminderBanner
 *
 * User-tunable weigh-in nudge. Defaults to Monday + Friday (twice-weekly)
 * per owner canon Feb 18, 2026: "The reminder is too aggressive. 1 time
 * Monday and 1 time Friday is fine. But allowing the user to choose is
 * better."
 *
 * Behavior:
 *   • Banner fires only on the user's chosen days (default Mon + Fri).
 *   • Dismissal is per-DAY — dismissing Monday's ping does NOT silence
 *     Friday's ping in the same week.
 *   • ⚙️ button toggles a lightweight preferences popover so the user
 *     can pick their own days or disable reminders entirely.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Settings2, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAY_OPTIONS = [
  { dow: 0, short: "Mon", full: "Monday" },
  { dow: 1, short: "Tue", full: "Tuesday" },
  { dow: 2, short: "Wed", full: "Wednesday" },
  { dow: 3, short: "Thu", full: "Thursday" },
  { dow: 4, short: "Fri", full: "Friday" },
  { dow: 5, short: "Sat", full: "Saturday" },
  { dow: 6, short: "Sun", full: "Sunday" },
];

export default function WeighInReminderBanner() {
  const [state, setState] = useState(null);
  const [dismissing, setDismissing] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const [savingPrefs, setSavingPrefs] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await axios.get(`${API}/hero/weigh-in-reminder`);
      setState(data || null);
      if (data?.prefs) setPrefs(data.prefs);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { refresh(); }, []);

  const dismiss = async () => {
    setDismissing(true);
    try {
      const { data } = await axios.post(`${API}/hero/weigh-in-reminder/dismiss`);
      setState((s) => (s ? { ...s, show: false, dismissed_today: true } : s));
      toast.success(`Reminder tucked away — see you next ${nextDayLabel()}.`);
    } catch (e) {
      toast.error("Couldn't dismiss reminder.");
    } finally { setDismissing(false); }
  };

  const nextDayLabel = () => {
    const days = prefs?.days || [0, 4];
    const today = state?.day_of_week ?? new Date().getDay();
    // JS getDay: Sun=0..Sat=6 ; backend uses Mon=0..Sun=6 — convert
    const backendToday = (today === 0) ? 6 : (today - 1);
    const next = days.find((d) => d > backendToday);
    const wrapped = next ?? days[0];
    return DAY_OPTIONS[wrapped]?.full || "reminder day";
  };

  const scrollToBio = () => {
    document.querySelector('[data-testid="hero-in-weight"]')?.scrollIntoView({
      behavior: "smooth", block: "center",
    });
    setTimeout(() => {
      document.querySelector('[data-testid="hero-in-weight"]')?.focus?.();
    }, 500);
  };

  const toggleDay = (dow) => {
    if (!prefs) return;
    const on = prefs.days.includes(dow);
    const next = on ? prefs.days.filter((d) => d !== dow) : [...prefs.days, dow].sort((a, b) => a - b);
    setPrefs({ ...prefs, days: next.length ? next : prefs.days });   // never allow empty
  };

  const toggleEnabled = () => {
    if (!prefs) return;
    setPrefs({ ...prefs, enabled: !prefs.enabled });
  };

  const savePrefs = async () => {
    if (!prefs) return;
    setSavingPrefs(true);
    try {
      const { data } = await axios.put(`${API}/hero/weigh-in-reminder/prefs`, {
        enabled: prefs.enabled,
        days:    prefs.days,
      });
      setPrefs(data);
      toast.success(
        prefs.enabled
          ? `Reminder set for ${data.day_labels.join(" & ")}.`
          : "Reminders paused."
      );
      setPrefsOpen(false);
      // re-fetch state so banner reflects the new schedule immediately
      refresh();
    } catch (e) {
      toast.error("Couldn't save reminder prefs.");
    } finally { setSavingPrefs(false); }
  };

  // Component visibility:
  //   • Full banner  — when state.show is true (a reminder is active today)
  //   • Prefs popup  — when the user explicitly opens the prefs panel
  //   • Mini gear    — otherwise (always available, so users can tune
  //                    reminders on non-reminder days too)
  if (!state) return null;

  const label = state.day_label || "Weigh-In";

  // Compact mode: no active reminder + prefs closed → tiny gear entry
  if (!state.show && !prefsOpen) {
    return (
      <div className="flex justify-end mb-2" data-testid="weigh-in-reminder-mini">
        <button
          onClick={() => setPrefsOpen(true)}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] uppercase tracking-widest text-[#A78BFA] hover:text-white hover:bg-[#6D28D9]/20 transition-colors"
          title="Weigh-in reminder preferences"
          data-testid="weigh-in-reminder-mini-btn"
        >
          <Settings2 size={11} /> Weigh-in reminders
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-[#6D28D9]/50 bg-gradient-to-r from-[#1e0a3a] via-[#231045] to-[#0f0630] p-3 mb-4 shadow-[0_0_18px_rgba(109,40,217,0.18)]"
         data-testid="weigh-in-reminder-banner">
      <div className="flex items-start gap-3">
        <div className="text-2xl leading-none pt-0.5">📅</div>
        <div className="flex-1">
          <div className="flex items-center justify-between mb-1">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#A78BFA] font-bold">
              {label} Weigh-In
            </div>
            <button
              onClick={() => setPrefsOpen((v) => !v)}
              className="p-1 rounded hover:bg-white/5 text-[#A78BFA] hover:text-white transition-colors"
              title="Reminder preferences"
              data-testid="weigh-in-reminder-prefs-btn"
            >
              <Settings2 size={13} />
            </button>
          </div>
          {state.show && (
            <>
              <div className="text-[12px] text-neutral-200 leading-snug mb-2">
                {state.message}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={scrollToBio}
                  className="px-3 py-1.5 rounded-lg bg-gradient-to-br from-[#6D28D9] to-[#7C3AED] text-white text-[11px] font-bold hover:brightness-110"
                  data-testid="weigh-in-reminder-log-btn"
                >
                  Log Now
                </button>
                <button
                  onClick={dismiss}
                  disabled={dismissing}
                  className="px-3 py-1.5 rounded-lg bg-black/40 border border-neutral-800 text-[11px] text-neutral-400 hover:text-neutral-200 hover:bg-black/60 disabled:opacity-50"
                  data-testid="weigh-in-reminder-dismiss-btn"
                >
                  {dismissing ? "…" : "Not today"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {prefsOpen && prefs && (
        <div className="mt-3 pt-3 border-t border-[#6D28D9]/30" data-testid="weigh-in-reminder-prefs-panel">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#A78BFA] font-bold">
              Reminder Preferences
            </div>
            <button
              onClick={() => setPrefsOpen(false)}
              className="p-1 rounded hover:bg-white/5 text-neutral-400 hover:text-white"
              data-testid="weigh-in-reminder-prefs-close"
            >
              <X size={12} />
            </button>
          </div>

          <label className="flex items-center gap-2 mb-2 cursor-pointer">
            <input
              type="checkbox"
              checked={!!prefs.enabled}
              onChange={toggleEnabled}
              className="accent-[#7C3AED]"
              data-testid="weigh-in-reminder-prefs-enabled"
            />
            <span className="text-[11px] text-neutral-200">Nudge me on my chosen days</span>
          </label>

          <div className={`flex flex-wrap gap-1.5 mb-3 ${prefs.enabled ? "" : "opacity-40 pointer-events-none"}`}>
            {DAY_OPTIONS.map((d) => {
              const on = prefs.days.includes(d.dow);
              return (
                <button
                  key={d.dow}
                  onClick={() => toggleDay(d.dow)}
                  className={`px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider border transition-colors ${
                    on
                      ? "bg-[#6D28D9] border-[#7C3AED] text-white"
                      : "bg-black/30 border-neutral-800 text-neutral-500 hover:text-neutral-300 hover:border-neutral-600"
                  }`}
                  data-testid={`weigh-in-reminder-prefs-day-${d.dow}`}
                >
                  {d.short}
                </button>
              );
            })}
          </div>

          <div className="flex gap-2">
            <button
              onClick={savePrefs}
              disabled={savingPrefs}
              className="px-3 py-1.5 rounded-lg bg-gradient-to-br from-[#6D28D9] to-[#7C3AED] text-white text-[11px] font-bold hover:brightness-110 disabled:opacity-50"
              data-testid="weigh-in-reminder-prefs-save"
            >
              {savingPrefs ? "Saving…" : "Save"}
            </button>
            <button
              onClick={() => { setPrefs(state?.prefs || prefs); setPrefsOpen(false); }}
              className="px-3 py-1.5 rounded-lg bg-black/40 border border-neutral-800 text-[11px] text-neutral-400 hover:text-neutral-200 hover:bg-black/60"
              data-testid="weigh-in-reminder-prefs-cancel"
            >
              Cancel
            </button>
          </div>
          <div className="text-[10px] text-neutral-500 mt-2">
            Default is Monday + Friday. Pick any days you like — or turn it off entirely.
          </div>
        </div>
      )}
    </div>
  );
}
